/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { OrderSearchListFilterModel } from 'app/modules/order/order-search/order-search/models/order-search-list-filter.model';
import { OrderSearchListService } from 'app/modules/order/order-search/order-search/services/order-search-list.service';
import { FilterFieldsBaseComponent } from 'app/shared/components/list-filter/filter-fields-base.component';
import { ListFilterIntermediaryService } from 'app/shared/components/list-filter/list-filter-intermediary-service.component';
import { canFilterMultiselect } from 'app/shared/helpers/multi-select-dropdown-helper';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { Filter, FilterOperatorType } from 'app/shared/models/filter.model';
import { LookupList } from 'app/shared/models/lookup-list.model';
import { UiStateService } from 'app/shared/services/ui-state.service';
import moment from 'moment';
import { OrderListType } from '../models/order-list-type';
import { OrderSearchListFilterResponseModel } from '../models/order-search-list-filter-response.model';

@Component({
	selector: 'order-search-list-filter',
	templateUrl: './order-search-list-filter.component.html',
	styles: [
		`
			:host ::ng-deep mat-form-field {
				min-width: 245px;
				max-width: 245px;
				width: 245px;
			}
			.filter-row {
				flex-wrap: wrap;
			}
			.bottom-padding-15px {
				padding-bottom: 15px;
			}
			.no-bottom-padding {
				margin-bottom: 0;
			}
		`,
	],
	providers: [{ provide: FilterFieldsBaseComponent, useExisting: forwardRef(() => OrderSearchListFilterComponent) }],
	standalone: false,
})
export class OrderSearchListFilterComponent extends FilterFieldsBaseComponent implements OnInit {
	model: OrderSearchListFilterModel = new OrderSearchListFilterModel();
	@Input() customerId: number;

	ticketTypeLookupData: any[];
	paymentPlanLookupData: any[];
	eventGenreLookupData: any[];
	eventStatusLookupData: any[];
	@ViewChild('allTicketTypesSelected') private allTicketTypesSelected: MatOption;
	@ViewChild('allPaymentPlansSelected') private allPaymentPlansSelected: MatOption;
	@ViewChild('allEventGenresSelected') private allEventGenresSelected: MatOption;
	@ViewChild('allEventStatusesSelected') private allEventStatusesSelected: MatOption;

	@Output() isOrganiserUser = new EventEmitter<boolean>();
	organiserUser: boolean;

	get listType(): OrderListType {
		if (this.hasCustomerId) {
			return OrderListType.CustomerOrderList;
		}
		if (this.organiserUser) {
			return OrderListType.OrganiserOrderList;
		}
		return OrderListType.DefaultOrderList;
	}

	get hasCustomerId(): boolean {
		return this.customerId != null && this.customerId > 0;
	}

	get isCustomerOrderList(): boolean {
		return this.listType === OrderListType.CustomerOrderList;
	}
	get isOrganiserOrderList(): boolean {
		return this.listType === OrderListType.OrganiserOrderList;
	}
	get isDefaultOrderList(): boolean {
		return this.listType === OrderListType.DefaultOrderList;
	}

	get orderNumber(): AbstractControl {
		return this.dataForm.controls['orderNumber'];
	}
	get organiser(): AbstractControl {
		return this.dataForm.controls['organiser'];
	}
	get event(): AbstractControl {
		return this.dataForm.controls['event'];
	}
	get ticketType(): AbstractControl {
		return this.dataForm.controls['ticketType'];
	}
	get venue(): AbstractControl {
		return this.dataForm.controls['venue'];
	}
	get customer(): AbstractControl {
		return this.dataForm.controls['customer'];
	}
	get eventDateFrom(): AbstractControl {
		return this.dataForm.controls['eventDateFrom'];
	}
	get eventDateTo(): AbstractControl {
		return this.dataForm.controls['eventDateTo'];
	}
	get orderDateFrom(): AbstractControl {
		return this.dataForm.controls['orderDateFrom'];
	}
	get orderDateTo(): AbstractControl {
		return this.dataForm.controls['orderDateTo'];
	}
	get paymentPlan(): AbstractControl {
		return this.dataForm.controls['paymentPlan'];
	}
	get eventGenre(): AbstractControl {
		return this.dataForm.controls['eventGenre'];
	}
	get eventStatus(): AbstractControl {
		return this.dataForm.controls['eventStatus'];
	}

	private uiStateKey = 'OrderSearchListFilter';

	constructor(
		private fb: FormBuilder,
		private uiStateService: UiStateService,
		private service: OrderSearchListService,
		private formHelperService: ReactiveFormHelperService,
		protected listFilterIntermediaryService: ListFilterIntermediaryService,
	) {
		super(listFilterIntermediaryService);
	}
	createGroupedLookupData(list: LookupList): any[] {
		let model: LookupList[] = [];

		let entryTickets = {} as LookupList;
		let nonEntryTickets = {} as LookupList;
		let glampingTickets = {} as LookupList;

		entryTickets.name = 'Entry';
		entryTickets.items = list.items
			.filter((item) => item.data.entryTicketType === true)
			.map((item) => {
				return item;
			});

		nonEntryTickets.name = 'Non Entry';
		nonEntryTickets.items = list.items
			.filter((item) => item.data.nonEntryTicketType === true)
			.map((item) => {
				return item;
			});

		glampingTickets.name = 'Glamping';
		glampingTickets.items = list.items
			.filter((item) => item.data.glampingTicketType === true)
			.map((item) => {
				return item;
			});

		if (entryTickets.items.length > 0) {
			model.push(entryTickets);
		}
		if (nonEntryTickets.items.length > 0) {
			model.push(nonEntryTickets);
		}
		if (glampingTickets.items.length > 0) {
			model.push(glampingTickets);
		}

		return model;
	}

	ngOnInit() {
		const model = this.getModelFromStorageOrDefault();

		this.service.getFilterLookupData().then((response: OrderSearchListFilterResponseModel) => {
			this.ticketTypeLookupData = this.createGroupedLookupData(response.ticketTypeLookupData);
			this.paymentPlanLookupData = this.formHelperService.createLookupData(response.paymentPlanLookupData);
			this.eventGenreLookupData = this.formHelperService.createLookupData(response.eventGenreLookupData);
			this.eventStatusLookupData = this.formHelperService.createLookupData(response.eventStatusLookupData);

			this.organiserUser = response.isOrganiserUser;
			this.isOrganiserUser.emit(response.isOrganiserUser);
			this.checkAllMultiSelectsForAllValue();
		});

		this.dataForm = this.fb.group({
			orderNumber: [model.orderNumber],
			organiser: [model.organiser],
			event: [model.event],
			ticketType: [model.ticketType],
			venue: [model.venue],
			customer: [model.customer],
			eventDateFrom: [model.eventDateFrom],
			eventDateTo: [model.eventDateTo],
			orderDateFrom: [model.orderDateFrom],
			orderDateTo: [model.orderDateTo],
			paymentPlan: [model.paymentPlan],
			eventGenre: [model.eventGenre],
			eventStatus: [model.eventStatus],
		});

		this.dataForm.markAsPristine();
	}

	ngAfterViewInit() {
		this.notifyFilterChanged();
	}

	private checkAllMultiSelectsForAllValue() {
		this.checkIfGroupedMultiSelectHaveAllSelected(this.ticketType, this.ticketTypeLookupData, this.allTicketTypesSelected);
		this.checkIfMultiSelectHaveAllSelected(this.paymentPlan, this.paymentPlanLookupData, this.allPaymentPlansSelected);
		this.checkIfMultiSelectHaveAllSelected(this.eventGenre, this.eventGenreLookupData, this.allEventGenresSelected);
		this.checkIfMultiSelectHaveAllSelected(this.eventStatus, this.eventStatusLookupData, this.allEventStatusesSelected);
	}

	resetToDefault() {
		this.dataForm.controls['orderNumber'].setValue(this.model.orderNumber);
		this.dataForm.controls['organiser'].setValue(this.model.organiser);
		this.dataForm.controls['event'].setValue(this.model.event);
		this.dataForm.controls['ticketType'].setValue(this.model.ticketType);
		this.dataForm.controls['venue'].setValue(this.model.venue);
		this.dataForm.controls['customer'].setValue(this.model.customer);
		this.dataForm.controls['eventDateFrom'].setValue(this.model.eventDateFrom);
		this.dataForm.controls['eventDateTo'].setValue(this.model.eventDateTo);
		this.dataForm.controls['orderDateFrom'].setValue(this.model.orderDateFrom);
		this.dataForm.controls['orderDateTo'].setValue(this.model.orderDateTo);
		this.dataForm.controls['paymentPlan'].setValue(this.model.paymentPlan);
		this.dataForm.controls['eventGenre'].setValue(this.model.eventGenre);
		this.dataForm.controls['eventStatus'].setValue(this.model.eventStatus);

		this.checkAllMultiSelectsForAllValue();
	}

	private persistFilterModel() {
		const stateModel = new OrderSearchListFilterModel(
			this.orderNumber.value,
			this.organiser.value,
			this.event.value,
			this.ticketType.value,
			this.venue.value,
			this.customer.value,
			this.eventDateFrom.value,
			this.eventDateTo.value,
			this.orderDateFrom.value,
			this.orderDateTo.value,
			this.paymentPlan.value,
			this.eventGenre.value,
			this.eventStatus.value,
		);
		this.uiStateService.setUiState(this.uiStateKey, stateModel);
	}

	getFilters(): Array<Filter> {
		this.persistFilterModel();
		const filters = new Array<Filter>();

		if (this.orderNumber.value) {
			filters.push(new Filter('OrderNumber', FilterOperatorType.Contains, this.orderNumber.value));
		}

		if (!this.isOrganiserOrderList && this.organiser.value) {
			filters.push(new Filter('Organiser', FilterOperatorType.Contains, this.organiser.value));
		}
		if (this.event.value) {
			filters.push(new Filter('Event', FilterOperatorType.Contains, this.event.value));
		}
		if (canFilterMultiselect('ticketType', this.dataForm)) {
			filters.push(new Filter('TicketType', FilterOperatorType.Equals, this.ticketType.value));
		}
		if (this.venue.value) {
			filters.push(new Filter('Venue', FilterOperatorType.Contains, this.venue.value));
		}
		if (!this.isCustomerOrderList && this.customer.value) {
			filters.push(new Filter('CustomerName', FilterOperatorType.Contains, this.customer.value));
		}
		if (this.isCustomerOrderList) {
			filters.push(new Filter('CustomerId', FilterOperatorType.Equals, this.customerId));
		}
		if (this.eventDateFrom.value) {
			const date = moment(this.eventDateFrom.value).startOf('day').toDate();
			filters.push(new Filter('EventDate', FilterOperatorType.GreaterThanEqual, date));
		}
		if (this.eventDateTo.value) {
			const date = moment(this.eventDateTo.value).endOf('day').toDate();
			filters.push(new Filter('EventDate', FilterOperatorType.LessThanEqual, date));
		}
		if (this.orderDateFrom.value) {
			const date = moment(this.orderDateFrom.value).startOf('day').toDate();
			filters.push(new Filter('OrderDate', FilterOperatorType.GreaterThanEqual, date));
		}
		if (this.orderDateTo.value) {
			const date = moment(this.orderDateTo.value).endOf('day').toDate();
			filters.push(new Filter('OrderDate', FilterOperatorType.LessThanEqual, date));
		}
		if (this.paymentPlan.value && this.paymentPlan.value !== 0) {
			filters.push(new Filter('PaymentPlanEnum', FilterOperatorType.Equals, this.paymentPlan.value));
		}
		if (canFilterMultiselect('eventGenre', this.dataForm)) {
			filters.push(new Filter('EventGenre', FilterOperatorType.Equals, this.eventGenre.value));
		}
		if (canFilterMultiselect('eventStatus', this.dataForm)) {
			filters.push(new Filter('EventStatus', FilterOperatorType.Equals, this.eventStatus.value));
		}

		return filters;
	}

	private getModelFromStorageOrDefault(): OrderSearchListFilterModel {
		let model = this.uiStateService.getUiStateForKey(this.uiStateKey);

		if (model == null) {
			model = new OrderSearchListFilterModel();
		}

		return model;
	}
}
